/** @jsxImportSource @emotion/react */
import { useCallback, useContext, useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { type Chat, ChatActionType, type ChatResponseType } from '@trinity/chat';
import {
  CancelAction,
  DownloadQueryHint,
} from '@trinity/chat-components/components/presentation/Message.QueryHint/index.js';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  ActiveChatSessionContext,
  KolSearchFormContext,
  KolSearchIdentifierContext,
  KolSearchRecommendationsContext,
  SubmitActionContext,
} from './context';
import { useDownloadHcpResults } from './hooks/useDownloadHcpResults';
import { KolResultsContainer, KolResultsTable } from './KolSearch.Table';
import { KolTableContainer } from './KolTableContainer';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { MessageContainer } from './Message.System.Wrapper';

type Props = {
  queryIdentifier: string;
  response: Chat.QueryResponse<ChatResponseType.HcpSearchResults>;
  disabled?: boolean;
};

export const HcpSearchResultsResponse = ({ queryIdentifier, response, disabled = false }: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const chat = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();

  const hasResults = response.data.results.length > 0;

  const searchForm = useMemo(
    () => ({
      kolId: null,
      name: response.data.search.name ?? '',
      location: response.data.search.location
        ? {
            id: '',
            latitude: response.data.search.location.latitude,
            longitude: response.data.search.location.longitude,
            name: response.data.search.location.name ?? '',
          }
        : undefined,
    }),
    [response.data.search.location, response.data.search.name]
  );

  const recommendations = useMemo(
    () => ({
      status: 'skipped' as const,
      items: [],
      total: 0,
    }),
    []
  );

  const handleSelectProfile = useCallback(
    (item: { id: number; name: string }) => {
      if (disabled) {
        return;
      }

      submitAction(
        {
          type: ChatActionType.SelectHcpSubject,
          payload: {
            hcpId: item.id,
            hcpName: item.name,
          },
        },
        `Show me the profile for ${item.name}.`
      );
    },
    [disabled, submitAction]
  );

  const handleViewRecommended = useCallback(() => {
    if (disabled) {
      return;
    }

    submitAction(
      {
        type: ChatActionType.ViewRecommendedHcps,
      },
      'Show me recommended HCPs.'
    );
  }, [disabled, submitAction]);

  const { download: downloadHcpResults } = useDownloadHcpResults({
    chatInstanceId: chat.id,
    queryIdentifier,
    brandIdentifier,
  });

  return (
    <MessageContainer>
      <BasicSystemUserMessage bodyStyles={css({ maxWidth: '100%' })}>
        <Body>
          <Message>{buildMessageFilter(response.data.search)}</Message>
          {hasResults ? (
            <KolSearchFormContext.Provider value={searchForm}>
              <KolSearchRecommendationsContext.Provider value={recommendations}>
                <KolSearchIdentifierContext.Provider value={queryIdentifier}>
                  <KolTableContainer pageSize={10}>
                    <KolResultsContainer records={response.data.results}>
                      <StyledResultsTable isInteractive={!disabled} onSelectProfile={handleSelectProfile} />
                    </KolResultsContainer>
                  </KolTableContainer>
                </KolSearchIdentifierContext.Provider>
              </KolSearchRecommendationsContext.Provider>
            </KolSearchFormContext.Provider>
          ) : (
            <NoResults>No HCPs were found for your search criteria.</NoResults>
          )}

          {hasResults ? <StyledDownloadButton value="Download List" onClick={downloadHcpResults} disabled={disabled} /> : null}
          <StyledCancelButton displayValue="Start a new search" onClick={handleViewRecommended} disabled={disabled} />
          <StyledCancelButton displayValue="View recommended HCPs" onClick={handleViewRecommended} disabled={disabled} />
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

function buildMessageFilter(search: Chat.QueryResponse<ChatResponseType.HcpSearchResults>['data']['search']) {
  const filters: string[] = [];

  if (search.name) {
    filters.push(`matching the name "${search.name}"`);
  }

  if (search.location) {
    const locationLabel =
      search.location.name || `(${search.location.latitude}, ${search.location.longitude})`;
    filters.push(`near ${locationLabel}`);
  }

  if (!filters.length) {
    return 'Sure, I found the following HCPs.';
  }

  return `Sure, I found the following HCPs ${filters.join(' and ')}.`;
}

const StyledResultsTable = styled(KolResultsTable)({
  marginTop: 15,
});

const StyledDownloadButton = styled(DownloadQueryHint)({
  marginTop: 15,
});

const StyledCancelButton = styled(CancelAction)({
  marginTop: 15,
});

const Body = styled.div({
  width: '100%',
});

const Message = styled.div({
  marginBottom: 15,
});

const NoResults = styled.div(({ theme }) => ({
  marginTop: 10,
  fontFamily: theme.fonts.regular,
}));
